/**
 * Team Spain v0 — Servicio de Captura en Campo (M4)
 *
 * Implementa la lógica de negocio, derivación de eventos del SPORT_CONTRACT,
 * persistencia transaccional y colaboración online multi-dispositivo.
 */

import { supabase } from '../../lib/supabase'
import { offlineIndexedDb } from '../../lib/offline/indexedDb'
import { syncManager } from '../../lib/offline/syncManager'
import {
  TeamBlockCapture,
  FieldSnap,
  SnapParticipant,
  SnapEvent,
  SnapAnnotation,
  MatchContext,
  VideoSegment,
  CaptureWithSnapCount,
} from '../../types/fieldCapture'
import {
  LineupSlot,
  PrimaryResult,
  SnapMode,
  PracticeScript,
  LineupTemplate,
  RosterAthleteOption,
  OffensivePlay,
  DefensiveCall,
} from '../../types/sports'
import { PreparedPackage, LocalSnap, OutboxOperation } from '../../types/offline'

export class FieldCaptureService {
  /**
   * Genera eventos derivados canónicos según el contrato deportivo (SPORT_CONTRACT)
   * Garantiza que un resultado derive automáticamente sus eventos sin duplicidades.
   */
  deriveEventsFromResult(
    primaryResult: PrimaryResult,
    passerId?: string | null,
    receiverId?: string | null,
    tacklerId?: string | null
  ): SnapEvent[] {
    const events: SnapEvent[] = []

    if (primaryResult === 'pass_complete') {
      events.push({
        event_type: 'pass_attempt',
        actor_athlete_id: passerId || null,
        target_athlete_id: receiverId || null,
        unit: 'offense',
        is_derived: true,
      })
      events.push({
        event_type: 'pass_completion',
        actor_athlete_id: passerId || null,
        target_athlete_id: receiverId || null,
        unit: 'offense',
        is_derived: true,
      })
      events.push({
        event_type: 'target',
        actor_athlete_id: passerId || null,
        target_athlete_id: receiverId || null,
        unit: 'offense',
        is_derived: true,
      })
      events.push({
        event_type: 'catch',
        actor_athlete_id: receiverId || null,
        target_athlete_id: null,
        unit: 'offense',
        is_derived: true,
      })
    } else if (primaryResult === 'pass_incomplete') {
      events.push({
        event_type: 'pass_attempt',
        actor_athlete_id: passerId || null,
        target_athlete_id: receiverId || null,
        unit: 'offense',
        is_derived: true,
      })
      if (receiverId) {
        events.push({
          event_type: 'target',
          actor_athlete_id: passerId || null,
          target_athlete_id: receiverId,
          unit: 'offense',
          is_derived: true,
        })
      }
    } else if (primaryResult === 'interception') {
      events.push({
        event_type: 'pass_attempt',
        actor_athlete_id: passerId || null,
        unit: 'offense',
        is_derived: true,
      })
      events.push({
        event_type: 'interception_thrown',
        actor_athlete_id: passerId || null,
        unit: 'offense',
        is_derived: true,
      })
      events.push({
        event_type: 'interception',
        actor_athlete_id: tacklerId || null,
        unit: 'defense',
        is_derived: true,
      })
    } else if (primaryResult === 'sack') {
      events.push({
        event_type: 'sack',
        actor_athlete_id: tacklerId || null,
        target_athlete_id: passerId || null,
        unit: 'defense',
        is_derived: true,
      })
    }

    return events
  }

  /**
   * Obtiene o crea una sesión de captura para un bloque de entrenamiento
   */
  async getOrCreateCaptureForBlock(params: {
    teamId: string
    blockId?: string | null
    sessionId?: string | null
    scriptId?: string | null
    title?: string
    mode?: SnapMode
    deviceId: string
    userId: string
  }): Promise<TeamBlockCapture> {
    if (!supabase) throw new Error('Supabase no inicializado')

    // 1. Buscar captura existente activa o pausada para este bloque
    if (params.blockId) {
      const { data: existing, error: searchErr } = await supabase
        .from('team_block_captures')
        .select('*')
        .eq('team_id', params.teamId)
        .eq('block_id', params.blockId)
        .in('status', ['active', 'paused'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (!searchErr && existing) {
        return existing as TeamBlockCapture
      }
    }

    // 2. Si no existe, inicializar con datos del guion o plantillas base
    let initialOffenseName = 'Ataque 1'
    let initialOffenseSlots: LineupSlot[] = []
    let initialOffenseTemplateId: string | undefined
    let initialDefenseName = 'Defensa A'
    let initialDefenseSlots: LineupSlot[] = []
    let initialDefenseTemplateId: string | undefined
    let initialTitle = params.title || 'Captura de Campo'
    let initialMode: SnapMode = params.mode || 'guided_practice'

    // Si viene vinculado a un guion, cargar sus repeticiones previstas
    if (params.scriptId) {
      const { data: scriptData } = await supabase
        .from('practice_scripts')
        .select('*, planned_reps(*)')
        .eq('id', params.scriptId)
        .single()

      if (scriptData) {
        initialTitle = scriptData.name
        initialMode = params.mode || (scriptData.mode as SnapMode)
        const sortedReps = (scriptData.planned_reps || []).sort(
          (a: { order_index: number }, b: { order_index: number }) => a.order_index - b.order_index
        )
        if (sortedReps.length > 0) {
          const firstRep = sortedReps[0]
          initialOffenseName = firstRep.offense_lineup_name
          initialOffenseSlots = (firstRep.offense_slots as LineupSlot[]) || []
          initialOffenseTemplateId = firstRep.offense_lineup_template_id
          initialDefenseName = firstRep.defense_lineup_name
          initialDefenseSlots = (firstRep.defense_slots as LineupSlot[]) || []
          initialDefenseTemplateId = firstRep.defense_lineup_template_id
        }
      }
    }

    // Si aún no tenemos quintetos completos, buscar las primeras plantillas activas del equipo
    if (initialOffenseSlots.length === 0 || initialDefenseSlots.length === 0) {
      const { data: templates } = await supabase
        .from('lineup_templates')
        .select('*, lineup_template_slots(*, athletes(*))')
        .eq('team_id', params.teamId)
        .eq('status', 'active')

      if (templates) {
        const offTmpl = templates.find((t) => t.unit === 'offense')
        if (offTmpl && initialOffenseSlots.length === 0) {
          initialOffenseName = offTmpl.name
          initialOffenseTemplateId = offTmpl.id
          initialOffenseSlots = (offTmpl.lineup_template_slots || []).map((s: {
            athlete_id: string
            position: string
            athletes?: { full_name?: string; first_name?: string; last_name?: string; dorsal_number?: number; primary_position?: string }
          }) => ({
            athlete_id: s.athlete_id,
            position: s.position,
            athlete_name: s.athletes?.full_name || `${s.athletes?.first_name || ''} ${s.athletes?.last_name || ''}`.trim() || 'Atleta',
            dorsal_number: s.athletes?.dorsal_number,
            primary_position: s.athletes?.primary_position,
          }))
        }

        const defTmpl = templates.find((t) => t.unit === 'defense')
        if (defTmpl && initialDefenseSlots.length === 0) {
          initialDefenseName = defTmpl.name
          initialDefenseTemplateId = defTmpl.id
          initialDefenseSlots = (defTmpl.lineup_template_slots || []).map((s: {
            athlete_id: string
            position: string
            athletes?: { full_name?: string; first_name?: string; last_name?: string; dorsal_number?: number; primary_position?: string }
          }) => ({
            athlete_id: s.athlete_id,
            position: s.position,
            athlete_name: s.athletes?.full_name || `${s.athletes?.first_name || ''} ${s.athletes?.last_name || ''}`.trim() || 'Atleta',
            dorsal_number: s.athletes?.dorsal_number,
            primary_position: s.athletes?.primary_position,
          }))
        }
      }
    }

    // Obtener organization_id
    const { data: teamData } = await supabase
      .from('teams')
      .select('organization_id')
      .eq('id', params.teamId)
      .single()

    const orgId = teamData?.organization_id || '11111111-1111-1111-1111-111111111111'

    const newCapturePayload = {
      organization_id: orgId,
      team_id: params.teamId,
      block_id: params.blockId || null,
      session_id: params.sessionId || null,
      script_id: params.scriptId || null,
      title: initialTitle,
      mode: initialMode,
      status: 'active',
      coordinator_device_id: params.deviceId,
      coordinator_user_id: params.userId,
      current_snap_sequence: 1,
      current_rep_index: 0,
      offense_lineup_template_id: initialOffenseTemplateId || null,
      offense_lineup_name: initialOffenseName,
      offense_lineup_is_custom: false,
      offense_slots: initialOffenseSlots,
      defense_lineup_template_id: initialDefenseTemplateId || null,
      defense_lineup_name: initialDefenseName,
      defense_lineup_is_custom: false,
      defense_slots: initialDefenseSlots,
      video_segments: [],
    }

    const { data: created, error: insertErr } = await supabase
      .from('team_block_captures')
      .insert(newCapturePayload)
      .select()
      .single()

    if (insertErr) throw new Error(insertErr.message)
    return created as TeamBlockCapture
  }

  /**
   * Carga el estado completo de una captura con todos sus snaps, participantes y eventos
   */
  async getCaptureDetails(captureId: string): Promise<{
    capture: TeamBlockCapture
    snaps: FieldSnap[]
  }> {
    if (!supabase) throw new Error('Supabase no inicializado')

    const { data: capture, error: capErr } = await supabase
      .from('team_block_captures')
      .select('*')
      .eq('id', captureId)
      .single()

    if (capErr) throw new Error(capErr.message)

    const { data: snapsData, error: snapErr } = await supabase
      .from('snaps')
      .select(`
        *,
        snap_participants(*),
        snap_events(*),
        snap_annotations(*)
      `)
      .eq('capture_id', captureId)
      .order('sequence', { ascending: true })

    if (snapErr) throw new Error(snapErr.message)

    const formattedSnaps: FieldSnap[] = (snapsData || []).map((s: Record<string, unknown>) => ({
      ...(s as unknown as FieldSnap),
      participants: (s.snap_participants as SnapParticipant[]) || [],
      events: (s.snap_events as SnapEvent[]) || [],
      annotations: (s.snap_annotations as SnapAnnotation[]) || [],
    }))

    return {
      capture: capture as TeamBlockCapture,
      snaps: formattedSnaps,
    }
  }

  /**
   * Guarda de forma transaccional un snap con sus 10 participantes y eventos derivados
   */
  async commitSnap(params: {
    snap: Partial<FieldSnap>
    participants: SnapParticipant[]
    events?: SnapEvent[]
    annotations?: SnapAnnotation[]
  }): Promise<FieldSnap> {
    if (!supabase) throw new Error('Supabase no inicializado')

    // Asegurar eventos derivados canónicos si hay un resultado primario
    const explicitEvents = params.events ? [...params.events] : []
    if (params.snap.primary_result) {
      const derived = this.deriveEventsFromResult(
        params.snap.primary_result,
        params.snap.passer_athlete_id,
        params.snap.receiver_athlete_id,
        params.snap.tackler_athlete_id
      )
      // Fusionar evitando duplicar exactamente por tipo de evento y actor
      for (const d of derived) {
        if (!explicitEvents.some((e) => e.event_type === d.event_type && e.unit === d.unit && e.actor_athlete_id === d.actor_athlete_id)) {
          explicitEvents.push(d)
        }
      }
    }

    const { data, error } = await supabase.rpc('commit_field_snap', {
      p_snap: params.snap,
      p_participants: params.participants,
      p_events: explicitEvents,
      p_annotations: params.annotations || [],
    })

    if (error) throw new Error(error.message)
    return data as FieldSnap
  }

  /**
   * Corrige el resultado de un snap de forma coherente revocando eventos derivados previos
   */
  async correctSnapResult(params: {
    snapId: string
    newPrimaryResult: PrimaryResult
    passerAthleteId?: string | null
    receiverAthleteId?: string | null
    tacklerAthleteId?: string | null
  }): Promise<FieldSnap> {
    if (!supabase) throw new Error('Supabase no inicializado')

    const { data, error } = await supabase.rpc('correct_field_snap_result', {
      p_snap_id: params.snapId,
      p_new_primary_result: params.newPrimaryResult,
      p_passer_athlete_id: params.passerAthleteId || null,
      p_receiver_athlete_id: params.receiverAthleteId || null,
      p_tackler_athlete_id: params.tacklerAthleteId || null,
    })

    if (error) throw new Error(error.message)
    return data as FieldSnap
  }

  /**
   * Traspaso explícito de coordinador entre dispositivos para evitar split-brain
   */
  async handoverCoordinator(captureId: string, newDeviceId: string, newUserId: string): Promise<boolean> {
    if (!supabase) throw new Error('Supabase no inicializado')

    const { data, error } = await supabase.rpc('handover_coordinator', {
      p_capture_id: captureId,
      p_new_device_id: newDeviceId,
      p_new_user_id: newUserId,
    })

    if (error) throw new Error(error.message)
    return Boolean(data)
  }

  /**
   * Actualiza los quintetos activos para los siguientes snaps
   */
  async updateActiveLineups(params: {
    captureId: string
    offenseLineupName: string
    offenseSlots: LineupSlot[]
    offenseIsCustom?: boolean
    defenseLineupName: string
    defenseSlots: LineupSlot[]
    defenseIsCustom?: boolean
  }): Promise<void> {
    if (!supabase) throw new Error('Supabase no inicializado')

    const { error } = await supabase
      .from('team_block_captures')
      .update({
        offense_lineup_name: params.offenseLineupName,
        offense_slots: params.offenseSlots,
        offense_lineup_is_custom: Boolean(params.offenseIsCustom),
        defense_lineup_name: params.defenseLineupName,
        defense_slots: params.defenseSlots,
        defense_lineup_is_custom: Boolean(params.defenseIsCustom),
        updated_at: new Date().toISOString(),
      })
      .eq('id', params.captureId)

    if (error) throw new Error(error.message)
  }

  /**
   * Actualiza el marcador o contexto de partido
   */
  async updateMatchContext(captureId: string, matchContext: MatchContext): Promise<void> {
    if (!supabase) throw new Error('Supabase no inicializado')

    const { error } = await supabase
      .from('team_block_captures')
      .update({
        match_context: matchContext,
        updated_at: new Date().toISOString(),
      })
      .eq('id', captureId)

    if (error) throw new Error(error.message)
  }

  /**
   * Añade o actualiza un segmento de vídeo vinculado al bloque
   */
  async updateVideoSegments(captureId: string, segments: VideoSegment[]): Promise<void> {
    if (!supabase) throw new Error('Supabase no inicializado')

    const { error } = await supabase
      .from('team_block_captures')
      .update({
        video_segments: segments,
        updated_at: new Date().toISOString(),
      })
      .eq('id', captureId)

    if (error) throw new Error(error.message)
  }

  /**
   * Anula un snap conservando su trazabilidad histórica (validity = 'void' o 'no_play')
   */
  async voidSnap(snapId: string, validity: 'void' | 'no_play' = 'void', reason?: string): Promise<void> {
    if (!supabase) throw new Error('Supabase no inicializado')

    const { error } = await supabase
      .from('snaps')
      .update({
        validity,
        review_notes: reason ? `[Anulado]: ${reason}` : '[Anulado]',
        status: 'finalized',
        updated_at: new Date().toISOString(),
      })
      .eq('id', snapId)

    if (error) throw new Error(error.message)
  }

  /**
   * Corrige integralmente los detalles de un snap pasado (resultado, yardas, down, eventos secundarios)
   */
  async updateSnapDetails(params: {
    snapId: string
    patch: Partial<FieldSnap>
    events?: SnapEvent[]
  }): Promise<FieldSnap> {
    if (!supabase) throw new Error('Supabase no inicializado')

    const { data, error } = await supabase.rpc('update_field_snap_details', {
      p_snap_id: params.snapId,
      p_patch: params.patch,
      p_events: params.events ?? null,
    })

    if (error) throw new Error(error.message)
    return data as FieldSnap
  }

  /**
   * Actualiza el marcador de la serie en bloques competitivos (Ataque vs Defensa)
   */
  async updateSeriesScore(params: {
    captureId: string
    showSeriesScore?: boolean
    offenseScore?: number
    defenseScore?: number
  }): Promise<void> {
    if (!supabase) throw new Error('Supabase no inicializado')

    const patch: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    }
    if (params.showSeriesScore !== undefined) patch.show_series_score = params.showSeriesScore
    if (params.offenseScore !== undefined) patch.series_score_offense = params.offenseScore
    if (params.defenseScore !== undefined) patch.series_score_defense = params.defenseScore

    const { error } = await supabase
      .from('team_block_captures')
      .update(patch)
      .eq('id', params.captureId)

    if (error) throw new Error(error.message)
  }

  /**
   * Actualiza el down y posición de campo de la serie
   */
  async updateDownAndYardLine(params: {
    captureId: string
    down: number
    yardLine: number
  }): Promise<void> {
    if (!supabase) throw new Error('Supabase no inicializado')

    const { error } = await supabase
      .from('team_block_captures')
      .update({
        current_down: params.down,
        current_yard_line: params.yardLine,
        updated_at: new Date().toISOString(),
      })
      .eq('id', params.captureId)

    if (error) throw new Error(error.message)
  }

  /**
   * Finaliza oficialmente un bloque o partido de captura
   */
  async finalizeCapture(captureId: string, notes?: string): Promise<TeamBlockCapture> {
    if (!supabase) throw new Error('Supabase no inicializado')

    const { data, error } = await supabase.rpc('finalize_capture', {
      p_capture_id: captureId,
      p_notes: notes || null,
    })

    if (error) throw new Error(error.message)
    return data as TeamBlockCapture
  }

  /**
   * Reabre de forma auditada un bloque o partido completado
   */
  async reopenCapture(captureId: string, reason?: string): Promise<TeamBlockCapture> {
    if (!supabase) throw new Error('Supabase no inicializado')

    const { data, error } = await supabase.rpc('reopen_capture', {
      p_capture_id: captureId,
      p_reason: reason || null,
    })

    if (error) throw new Error(error.message)
    return data as TeamBlockCapture
  }

  /**
   * Obtiene la lista completa de capturas (activas y finalizadas) para revisión de historial
   */
  async getAllCaptures(teamId: string): Promise<TeamBlockCapture[]> {
    if (!supabase) throw new Error('Supabase no inicializado')

    const { data, error } = await supabase
      .from('team_block_captures')
      .select('*')
      .eq('team_id', teamId)
      .order('created_at', { ascending: false })

    if (error) throw new Error(error.message)
    return (data || []) as TeamBlockCapture[]
  }

  /**
   * Obtiene la lista completa de capturas finalizadas para revisión snap a snap
   */
  async getCompletedCaptures(teamId: string): Promise<TeamBlockCapture[]> {
    if (!supabase) throw new Error('Supabase no inicializado')

    const { data, error } = await supabase
      .from('team_block_captures')
      .select('*')
      .eq('team_id', teamId)
      .eq('status', 'completed')
      .order('created_at', { ascending: false })

    if (error) throw new Error(error.message)
    return (data || []) as TeamBlockCapture[]
  }

  /**
   * Obtiene la lista completa de capturas del equipo con el conteo exacto de snaps,
   * ordenadas de más reciente a más antigua.
   * Sin depender de fechas de concentración ni de filtros de mes.
   */
  async getAllCapturesWithSnapCount(teamId: string): Promise<CaptureWithSnapCount[]> {
    if (!supabase) throw new Error('Supabase no inicializado')

    const { data: captures, error: capError } = await supabase
      .from('team_block_captures')
      .select('*')
      .eq('team_id', teamId)
      .order('created_at', { ascending: false })

    if (capError) throw new Error(capError.message)
    if (!captures || captures.length === 0) return []

    const captureIds = captures.map((c) => c.id)
    const { data: snaps, error: snapError } = await supabase
      .from('snaps')
      .select('id, capture_id')
      .in('capture_id', captureIds)

    if (snapError) throw new Error(snapError.message)

    const snapCountMap = new Map<string, number>()
    for (const snap of snaps || []) {
      const count = snapCountMap.get(snap.capture_id) || 0
      snapCountMap.set(snap.capture_id, count + 1)
    }

    return (captures as TeamBlockCapture[]).map((c) => ({
      ...c,
      snapCount: snapCountMap.get(c.id) || 0,
      snap_count: snapCountMap.get(c.id) || 0,
    }))
  }

  // ==========================================================================
  // M5: OPERACIONES OFFLINE, PREPARACIÓN Y SINCRONIZACIÓN
  // ==========================================================================

  /**
   * Prepara y descarga localmente todos los datos deportivos autorizados necesarios:
   * Contexto del bloque, guion y llamadas previstas, quintetos y jugadores, catálogo táctico.
   */
  async prepareCaptureForOffline(params: {
    captureId: string
    userId: string
    teamId: string
  }): Promise<PreparedPackage> {
    if (!supabase) throw new Error('Supabase no inicializado')

    // 1. Cargar captura desde el servidor
    const { data: capture, error: capErr } = await supabase
      .from('team_block_captures')
      .select('*')
      .eq('id', params.captureId)
      .single()

    if (capErr || !capture) {
      throw new Error(`Error al cargar captura para preparación offline: ${capErr?.message || 'No encontrada'}`)
    }

    // 2. Cargar guion si viene asociado
    let script: PracticeScript | null = null
    if (capture.script_id) {
      const { data: scriptData } = await supabase
        .from('practice_scripts')
        .select('*, planned_reps(*)')
        .eq('id', capture.script_id)
        .single()
      if (scriptData) {
        script = scriptData as PracticeScript
      }
    }

    // 3. Cargar plantillas de quintetos del equipo
    const { data: templates } = await supabase
      .from('lineup_templates')
      .select('*, lineup_template_slots(*, athletes(*))')
      .eq('team_id', params.teamId)
      .eq('status', 'active')

    // 4. Cargar atletas del equipo
    const { data: athletesData } = await supabase
      .from('athletes')
      .select('id, full_name, first_name, last_name, dorsal_number, primary_position, secondary_positions, unit')
      .eq('team_id', params.teamId)
      .eq('status', 'active')

    const athletes: RosterAthleteOption[] = (athletesData || []).map((a) => ({
      id: a.id,
      first_name: a.first_name || '',
      last_name: a.last_name || '',
      full_name: a.full_name || `${a.first_name || ''} ${a.last_name || ''}`.trim() || 'Atleta',
      dorsal_number: a.dorsal_number,
      primary_position: a.primary_position || 'ATH',
      unit: (a.unit === 'defense' ? 'defense' : 'offense') as 'offense' | 'defense',
    }))

    // 5. Cargar jugadas ofensivas
    const { data: playsData } = await supabase
      .from('offensive_plays')
      .select('*, offensive_concepts(*)')
      .eq('team_id', params.teamId)
      .eq('status', 'active')

    // 6. Cargar llamadas defensivas
    const { data: callsData } = await supabase
      .from('defensive_calls')
      .select('*')
      .eq('team_id', params.teamId)
      .eq('status', 'active')

    // 7. Cargar snaps ya existentes en el servidor para cachear localmente
    const { data: existingSnaps } = await supabase
      .from('snaps')
      .select('*, snap_participants(*), snap_events(*), snap_annotations(*)')
      .eq('capture_id', params.captureId)
      .order('sequence', { ascending: true })

    const pkg: PreparedPackage = {
      capture_id: params.captureId,
      team_id: params.teamId,
      user_id: params.userId,
      organization_id: capture.organization_id,
      capture: capture as TeamBlockCapture,
      script,
      lineup_templates: (templates || []) as LineupTemplate[],
      athletes,
      offensive_plays: (playsData || []) as OffensivePlay[],
      defensive_calls: (callsData || []) as DefensiveCall[],
      prepared_at: new Date().toISOString(),
      version: 1,
    }

    // Guardar paquete en IndexedDB
    await offlineIndexedDb.savePreparedPackage(pkg)

    // Guardar snaps existentes como ya sincronizados en IndexedDB
    if (existingSnaps && existingSnaps.length > 0) {
      for (const s of existingSnaps) {
        const localSnap: LocalSnap = {
          ...(s as unknown as FieldSnap),
          participants: s.snap_participants || [],
          events: s.snap_events || [],
          annotations: s.snap_annotations || [],
          sync_status: 'synced',
          client_created_at: s.created_at || new Date().toISOString(),
          local_revision: s.revision || 1,
        }
        const db = await offlineIndexedDb.getDb()
        await new Promise<void>((resolve, reject) => {
          const tx = db.transaction('local_snaps', 'readwrite')
          tx.objectStore('local_snaps').put(localSnap)
          tx.oncomplete = () => resolve()
          tx.onerror = () => reject(tx.error)
        })
      }
    }

    return pkg
  }

  /**
   * Comprueba si una captura está preparada localmente en IndexedDB
   */
  async isCapturePrepared(captureId: string): Promise<{
    prepared: boolean
    preparedAt?: string
    pkg?: PreparedPackage
  }> {
    try {
      const pkg = await offlineIndexedDb.getPreparedPackage(captureId)
      if (pkg) {
        return { prepared: true, preparedAt: pkg.prepared_at, pkg }
      }
    } catch {
      // IndexedDB no disponible o fallo temporal
    }
    return { prepared: false }
  }

  /**
   * Guarda de forma atómica y duradera un snap localmente en IndexedDB,
   * encola su operación en la outbox y dispara sincronización en segundo plano.
   */
  async saveSnapLocallyAndEnqueue(params: {
    snap: Partial<FieldSnap>
    participants: SnapParticipant[]
    events?: SnapEvent[]
    annotations?: SnapAnnotation[]
    teamId: string
    userId: string
    updatedCapture?: Partial<TeamBlockCapture> & { id: string }
  }): Promise<LocalSnap> {
    const snapId = params.snap.id || crypto.randomUUID()
    const operationId = crypto.randomUUID()
    const nowIso = new Date().toISOString()

    // Asegurar eventos derivados según SPORT_CONTRACT
    const explicitEvents = params.events ? [...params.events] : []
    if (params.snap.primary_result) {
      const derived = this.deriveEventsFromResult(
        params.snap.primary_result,
        params.snap.passer_athlete_id,
        params.snap.receiver_athlete_id,
        params.snap.tackler_athlete_id
      )
      for (const d of derived) {
        if (!explicitEvents.some((e) => e.event_type === d.event_type && e.unit === d.unit && e.actor_athlete_id === d.actor_athlete_id)) {
          explicitEvents.push(d)
        }
      }
    }

    const fullSnapPayload: FieldSnap = {
      id: snapId,
      organization_id: params.snap.organization_id || '',
      team_id: params.teamId,
      capture_id: params.snap.capture_id || '',
      sequence: params.snap.sequence || 1,
      mode: params.snap.mode || 'guided_practice',
      our_side: params.snap.our_side || 'both',
      status: 'finalized',
      validity: params.snap.validity || 'valid',
      coverage: params.snap.coverage || 'detailed',
      flagged_for_review: Boolean(params.snap.flagged_for_review),
      primary_result: params.snap.primary_result,
      down: params.snap.down,
      yard_line_start: params.snap.yard_line_start,
      yard_line_end: params.snap.yard_line_end,
      net_yards: params.snap.net_yards,
      is_first_down: params.snap.is_first_down,
      is_turnover: params.snap.is_turnover,
      turnover_type: params.snap.turnover_type,
      is_touchdown: Boolean(params.snap.is_touchdown),
      scorer_athlete_id: params.snap.scorer_athlete_id,
      had_lateral: Boolean(params.snap.had_lateral),
      points_awarded: params.snap.points_awarded || 0,
      conversion_type: params.snap.conversion_type,
      is_conversion: Boolean(params.snap.is_conversion),
      conversion_points_attempted: params.snap.conversion_points_attempted || null,
      conversion_result: params.snap.conversion_result || null,
      preceding_touchdown_snap_id: params.snap.preceding_touchdown_snap_id || null,
      offense_call_planned: params.snap.offense_call_planned,
      offense_call_actual: params.snap.offense_call_actual,
      defense_call_planned: params.snap.defense_call_planned,
      defense_call_actual: params.snap.defense_call_actual,
      offense_lineup_name: params.snap.offense_lineup_name || 'Ataque',
      defense_lineup_name: params.snap.defense_lineup_name || 'Defensa',
      passer_athlete_id: params.snap.passer_athlete_id,
      receiver_athlete_id: params.snap.receiver_athlete_id,
      tackler_athlete_id: params.snap.tackler_athlete_id,
      snap_started_at: params.snap.snap_started_at || nowIso,
      registered_at: nowIso,
      elapsed_seconds_in_block: params.snap.elapsed_seconds_in_block || 0,
      video_segment_id: params.snap.video_segment_id,
      video_timestamp_mark: params.snap.video_timestamp_mark,
      video_offset_seconds: params.snap.video_offset_seconds || 0,
      notes: params.snap.notes,
      revision: 1,
      created_at: nowIso,
      updated_at: nowIso,
      participants: params.participants,
      events: explicitEvents,
      annotations: params.annotations || [],
    }

    const localSnap: LocalSnap = {
      ...fullSnapPayload,
      sync_status: 'local',
      client_created_at: nowIso,
      local_revision: 1,
    }

    const outboxOp: OutboxOperation = {
      operation_id: operationId,
      entity_id: snapId,
      capture_id: params.snap.capture_id || '',
      entity_type: 'snap',
      operation_type: 'commit_snap',
      payload: {
        snap: fullSnapPayload,
        participants: params.participants,
        events: explicitEvents,
        annotations: params.annotations || [],
      },
      team_id: params.teamId,
      user_id: params.userId,
      client_created_at: nowIso,
      attempts: 0,
      last_attempt_at: null,
      last_error: null,
      status: 'pending',
      sequence_order: Date.now(),
    }

    // 1. Guardado atómico en IndexedDB
    await offlineIndexedDb.saveLocalSnapAtomic({
      snap: localSnap,
      outboxOp,
      updatedCapture: params.updatedCapture,
    })

    // 2. Disparar sincronización oportunista sin bloquear la UI
    syncManager.triggerSync('snap_save', params.teamId, params.userId).catch((err) => {
      console.warn('Sync oportunista en background:', err)
    })

    return localSnap
  }

  /**
   * Finaliza la sesión localmente y encola operación para el servidor
   */
  async saveCaptureFinalizeLocally(params: {
    captureId: string
    teamId: string
    userId: string
    notes?: string
  }): Promise<void> {
    const operationId = crypto.randomUUID()
    const nowIso = new Date().toISOString()

    const existingCapture = await offlineIndexedDb.getLocalCapture(params.captureId)
    if (existingCapture) {
      await offlineIndexedDb.saveLocalCapture({
        ...existingCapture,
        status: 'completed',
        ended_at: nowIso,
      })
    }

    const outboxOp: OutboxOperation = {
      operation_id: operationId,
      entity_id: params.captureId,
      capture_id: params.captureId,
      entity_type: 'capture',
      operation_type: 'finalize_capture',
      payload: {
        captureId: params.captureId,
        notes: params.notes,
      },
      team_id: params.teamId,
      user_id: params.userId,
      client_created_at: nowIso,
      attempts: 0,
      last_attempt_at: null,
      last_error: null,
      status: 'pending',
      sequence_order: Date.now(),
    }

    await offlineIndexedDb.enqueueOutboxOperation(outboxOp)
    syncManager.triggerSync('finalize_capture', params.teamId, params.userId).catch(() => {})
  }

  /**
   * Reabre la sesión localmente y encola operación para el servidor
   */
  async saveCaptureReopenLocally(params: {
    captureId: string
    teamId: string
    userId: string
    reason?: string
  }): Promise<void> {
    const operationId = crypto.randomUUID()
    const nowIso = new Date().toISOString()

    const existingCapture = await offlineIndexedDb.getLocalCapture(params.captureId)
    if (existingCapture) {
      await offlineIndexedDb.saveLocalCapture({
        ...existingCapture,
        status: 'active',
        ended_at: null,
        reopened_at: nowIso,
      })
    }

    const outboxOp: OutboxOperation = {
      operation_id: operationId,
      entity_id: params.captureId,
      capture_id: params.captureId,
      entity_type: 'capture',
      operation_type: 'reopen_capture',
      payload: {
        captureId: params.captureId,
        reason: params.reason,
      },
      team_id: params.teamId,
      user_id: params.userId,
      client_created_at: nowIso,
      attempts: 0,
      last_attempt_at: null,
      last_error: null,
      status: 'pending',
      sequence_order: Date.now(),
    }

    await offlineIndexedDb.enqueueOutboxOperation(outboxOp)
    syncManager.triggerSync('reopen_capture', params.teamId, params.userId).catch(() => {})
  }

  /**
   * Anula un snap localmente y encola operación para el servidor
   */
  async saveSnapVoidLocally(params: {
    snapId: string
    captureId: string
    validity: 'void' | 'no_play'
    reason?: string
    teamId: string
    userId: string
  }): Promise<FieldSnap> {
    const nowIso = new Date().toISOString()
    const operationId = crypto.randomUUID()

    const localSnaps = await offlineIndexedDb.getLocalSnaps(params.captureId)
    const targetSnap = localSnaps.find((s) => s.id === params.snapId)
    let updatedSnap: FieldSnap
    if (targetSnap) {
      const updatedLocal: LocalSnap = {
        ...targetSnap,
        validity: params.validity,
        review_notes: params.reason ? `[Anulado]: ${params.reason}` : '[Anulado]',
        updated_at: nowIso,
        sync_status: 'local',
      }
      const db = await offlineIndexedDb.getDb()
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction('local_snaps', 'readwrite')
        tx.objectStore('local_snaps').put(updatedLocal)
        tx.oncomplete = () => resolve()
        tx.onerror = () => reject(tx.error)
      })
      updatedSnap = updatedLocal
    } else {
      updatedSnap = {
        id: params.snapId,
        capture_id: params.captureId,
        team_id: params.teamId,
        validity: params.validity,
        review_notes: params.reason,
      } as FieldSnap
    }

    const outboxOp: OutboxOperation = {
      operation_id: operationId,
      entity_id: params.snapId,
      capture_id: params.captureId,
      entity_type: 'snap',
      operation_type: 'void_snap',
      payload: {
        snapId: params.snapId,
        validity: params.validity,
        reason: params.reason,
      },
      team_id: params.teamId,
      user_id: params.userId,
      client_created_at: nowIso,
      attempts: 0,
      last_attempt_at: null,
      last_error: null,
      status: 'pending',
      sequence_order: Date.now(),
    }

    await offlineIndexedDb.enqueueOutboxOperation(outboxOp)
    syncManager.triggerSync('void_snap', params.teamId, params.userId).catch(() => {})

    return updatedSnap
  }

  /**
   * Carga unificada de captura y snaps:
   * Fusiona datos del servidor con jugadas locales aún no sincronizadas (sin duplicidades)
   * o recurre a la base de datos IndexedDB si no hay conexión.
   */
  async getUnifiedCaptureDetails(params: {
    captureId: string
    teamId: string
    userId: string
  }): Promise<{
    capture: TeamBlockCapture
    snaps: FieldSnap[]
    isOffline: boolean
  }> {
    // 1. Intentar consultar servidor si hay red
    try {
      const serverDetails = await this.getCaptureDetails(params.captureId)
      // Cargar snaps locales de IndexedDB para ver si hay alguno pendiente
      const localSnaps = await offlineIndexedDb.getLocalSnaps(params.captureId)
      const unsyncedLocals = localSnaps.filter((ls) => ls.sync_status === 'local')

      // Fusionar evitando duplicados por snap ID
      const serverIds = new Set(serverDetails.snaps.map((s) => s.id))
      const mergedSnaps = [...serverDetails.snaps]
      for (const local of unsyncedLocals) {
        if (!serverIds.has(local.id)) {
          mergedSnaps.push(local)
        }
      }
      mergedSnaps.sort((a, b) => a.sequence - b.sequence)

      // Guardar metadatos en IndexedDB
      await offlineIndexedDb.saveLocalCapture(serverDetails.capture)

      return {
        capture: serverDetails.capture,
        snaps: mergedSnaps,
        isOffline: false,
      }
    } catch {
      // 2. Fallback a IndexedDB local
      const localCap = await offlineIndexedDb.getLocalCapture(params.captureId)
      const prepPkg = await offlineIndexedDb.getPreparedPackage(params.captureId)
      const capture = localCap || prepPkg?.capture

      if (!capture) {
        throw new Error(
          'Esta captura no está preparada para uso sin conexión. Conéctate a la red para cargarla.'
        )
      }

      const snaps = await offlineIndexedDb.getLocalSnaps(params.captureId)
      return {
        capture,
        snaps,
        isOffline: true,
      }
    }
  }
}

export const fieldCaptureService = new FieldCaptureService()
